import { create } from 'zustand'
import {
  getAllBrands,
  getAllNotes,
  getAllPerfumers,
  getAllCountries,
  getAllPrices,
  type IBrand,
  type INote,
  type IPerfumer,
  type ICountry,
  type IPrice
} from '../pocketbase/PocketbaseMetadataService'

interface PbMetadataState {
  brands: IBrand[]
  notes: INote[]
  perfumers: IPerfumer[]
  countries: ICountry[]
  prices: IPrice[]
  isLoading: boolean
  isLoaded: boolean
  error: string | null

  // Actions
  fetchAllMetadata: (force?: boolean) => Promise<void>
  getBrandById: (id: string) => IBrand | undefined
  getNoteById: (id: string) => INote | undefined
  getPerfumerById: (id: string) => IPerfumer | undefined
  getCountryById: (id: string) => ICountry | undefined
  getPriceById: (id: string) => IPrice | undefined
}

export const usePbMetadataStore = create<PbMetadataState>((set, get) => ({
  brands: [],
  notes: [],
  perfumers: [],
  countries: [],
  prices: [],
  isLoading: false,
  isLoaded: false,
  error: null,

  fetchAllMetadata: async (force?: boolean) => {
    // Don't fetch if already loaded, unless forced
    if (get().isLoaded && !force) {
      return
    }

    set({ isLoading: true, error: null })

    try {
      const [brands, notes, perfumers, countries, prices] = await Promise.all([
        getAllBrands(),
        getAllNotes(),
        getAllPerfumers(),
        getAllCountries(),
        getAllPrices(),
      ])

      set({
        brands,
        notes,
        perfumers,
        countries,
        prices,
        isLoading: false,
        isLoaded: true,
      })
    } catch (error) {
      console.error('Error fetching metadata:', error)
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch metadata',
      })
    }
  },

  getBrandById: (id: string) => {
    return get().brands.find(brand => brand.id === id)
  },

  getNoteById: (id: string) => {
    return get().notes.find(note => note.id === id)
  },

  getPerfumerById: (id: string) => {
    return get().perfumers.find(perfumer => perfumer.id === id)
  },

  getCountryById: (id: string) => {
    return get().countries.find(country => country.id === id)
  },

  getPriceById: (id: string) => {
    return get().prices.find(price => price.id === id)
  },
}))
